import React, { useState, useEffect, useRef } from 'react';

// ─── TRANSLATION LOADER ───────────────────────────────────────────────────────
//
// HOW IT WORKS:
//  1. Listens to the custom 'translationStart' window event fired by
//     window.changeLanguage() in LanguageController.
//  2. Shows a full-page overlay with a spinner immediately.
//  3. Listens to 'translationComplete' fired by the MutationObserver in
//     LanguageController when Google actually finishes translating.
//  4. Hides the overlay with a smooth fade-out.
//  5. Safety timeout (5s max) — if Google Translate never fires completion,
//     the loader hides itself so the UI never gets permanently stuck.
//
// USAGE:
//  Place <TranslationLoader /> once in your App.jsx or root layout,
//  alongside <LanguageController />.
//
//  Example:
//    <LanguageController />
//    <TranslationLoader />
//    <RouterProvider router={router} />
//
const TranslationLoader = () => {
  const [visible, setVisible] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const [langName, setLangName] = useState('');
  const safetyTimer = useRef(null);
  const fadeTimer = useRef(null);

  const LANG_NAMES = {
    en: 'English', es: 'Spanish', fr: 'French', de: 'German',
    hi: 'Hindi', pt: 'Portuguese', it: 'Italian', ru: 'Russian',
    ja: 'Japanese', 'zh-CN': 'Chinese', ar: 'Arabic', ko: 'Korean', tr: 'Turkish',
  };

  const dismiss = () => {
    // Fade out first, then fully unmount
    setFadeOut(true);
    fadeTimer.current = setTimeout(() => {
      setVisible(false);
      setFadeOut(false);
    }, 400); // matches the CSS transition duration
  };

  useEffect(() => {
    const onStart = (e) => {
      // Clear any existing timers from a previous translation
      clearTimeout(safetyTimer.current);
      clearTimeout(fadeTimer.current);

      const code = e.detail?.lang || '';
      setLangName(LANG_NAMES[code] || code.toUpperCase());
      setFadeOut(false);
      setVisible(true);

      // Safety fallback — never stay stuck longer than 5 seconds
      safetyTimer.current = setTimeout(dismiss, 5000);
    };

    const onComplete = () => {
      clearTimeout(safetyTimer.current);
      dismiss();
    };

    window.addEventListener('translationStart', onStart);
    window.addEventListener('translationComplete', onComplete);

    return () => {
      window.removeEventListener('translationStart', onStart);
      window.removeEventListener('translationComplete', onComplete);
      clearTimeout(safetyTimer.current);
      clearTimeout(fadeTimer.current);
    };
  }, []);

  if (!visible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        transition: 'opacity 0.4s ease',
        opacity: fadeOut ? 0 : 1,
        pointerEvents: fadeOut ? 'none' : 'all',
      }}
    >
      {/* Spinner ring */}
      <div style={{ position: 'relative', width: 56, height: 56, marginBottom: 20 }}>
        {/* Outer track */}
        <div style={{
          position: 'absolute', inset: 0,
          borderRadius: '50%',
          border: '4px solid #e5e7eb',
        }} />
        {/* Spinning arc */}
        <div style={{
          position: 'absolute', inset: 0,
          borderRadius: '50%',
          border: '4px solid transparent',
          borderTopColor: '#3b82f6',
          borderRightColor: '#3b82f6',
          animation: 'tl-spin 0.75s linear infinite',
        }} />
        {/* Globe icon in center */}
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 20,
        }}>
          🌐
        </div>
      </div>

      {/* Label */}
      <p
        className="notranslate"
        translate="no"
        style={{
          margin: 0,
          fontSize: 15,
          fontWeight: 600,
          color: '#1f2937',
          letterSpacing: '-0.01em',
        }}
      >
        {langName ? `Switching to ${langName}…` : 'Applying translation…'}
      </p>
      <p
        className="notranslate"
        translate="no"
        style={{ margin: '4px 0 0', fontSize: 12, color: '#9ca3af' }}
      >
        Please wait
      </p>

      {/* Keyframe injection — scoped inline so no stylesheet needed */}
      <style>{`
        @keyframes tl-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default TranslationLoader;